import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {Subject, Subscription, takeUntil} from 'rxjs';
import {OfferRuleModel} from '../../../../../model/offer-rule.model';
import {OfferRuleTypeEnumerator} from '../enumerators/offer-rule-type.enumerator';
import {RulePrivacyEnumerator} from '../enumerators/rule-privacy.enumerator';
import {VehicleTypeEnumerator} from '../enumerators/vehicle-type.enumerator';
import {
  QualifyingRuleSourceFacets,
  QualifyingRuleSourceFilters,
  QualifyingRuleSourceMaximumPriceSortDirection,
  QualifyingRuleSourcePage,
  QualifyingRuleSourceRow
} from '../models/qualifying-rule-source.model';
import {
  QualifyingRuleSourceDiscoveryRequest,
  QualifyingRuleSourceDiscoveryState,
  QualifyingRuleSourceDiscoveryStatus,
  QualifyingRuleSourceService
} from '../services/qualifying-rule-source.service';

interface SelectOption<T> {
  readonly label: string;
  readonly value: T;
}

interface RuleDetailsFormControls {
  ruleType: FormControl<OfferRuleTypeEnumerator>;
  privacy: FormControl<RulePrivacyEnumerator>;
  status: FormControl<boolean>;
  vehicleType: FormControl<VehicleTypeEnumerator>;
  year: FormControl<number | null>;
  make: FormControl<string | null>;
  model: FormControl<string | null>;
  trim: FormControl<string | null>;
  maximumPrice: FormControl<number | null>;
  vehicleLimit: FormControl<number | null>;
  dollarDiscount: FormControl<number | null>;
}

export type IpopRuleWorkflowRuleStatus = 'Active' | 'Inactive';

export interface IpopRuleWorkflowRuleDetailsValue {
  readonly ruleType: OfferRuleTypeEnumerator;
  readonly privacy: RulePrivacyEnumerator;
  readonly status: IpopRuleWorkflowRuleStatus;
  readonly vehicleType: VehicleTypeEnumerator;
  readonly year: number | null;
  readonly make: string | null;
  readonly model: string | null;
  readonly trim: string | null;
  readonly maximumPrice: number | null;
  readonly vehicleLimit: number | null;
  readonly dollarDiscount: number | null;
}

const activeStatus: IpopRuleWorkflowRuleStatus = 'Active';
const inactiveStatus: IpopRuleWorkflowRuleStatus = 'Inactive';
const defaultPage = 1;
const discoveryPageSize = 5;
const ascendingSortDirection: QualifyingRuleSourceMaximumPriceSortDirection = 'asc';
const descendingSortDirection: QualifyingRuleSourceMaximumPriceSortDirection = 'desc';
const pendingRequestId = 0;
export const anyTrimValue = 'Any';

const positiveSafeIntegerValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  let errors: ValidationErrors | null = null;
  const value: unknown = control.value;
  if (value !== null && (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0)) {
    errors = {positiveSafeInteger: true};
  }

  return errors;
};

const finiteNonNegativeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  let errors: ValidationErrors | null = null;
  const value: unknown = control.value;
  if (value !== null && (typeof value !== 'number' || !Number.isFinite(value) || value < 0)) {
    errors = {finiteNonNegative: true};
  }

  return errors;
};

const finitePositiveValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  let errors: ValidationErrors | null = null;
  const value: unknown = control.value;
  if (value !== null && (typeof value !== 'number' || !Number.isFinite(value) || value <= 0)) {
    errors = {finitePositive: true};
  }

  return errors;
};

const nonNegativeSafeIntegerValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  let errors: ValidationErrors | null = null;
  const value: unknown = control.value;
  if (value !== null && (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0)) {
    errors = {nonNegativeSafeInteger: true};
  }

  return errors;
};

@Component({
  selector: 'auto-ipop-rule-workflow-rule-details',
  templateUrl: './ipop-rule-workflow-rule-details.component.html',
  styleUrls: ['./ipop-rule-workflow-rule-details.component.scss'],
  standalone: false
})
export class IpopRuleWorkflowRuleDetailsComponent implements OnInit, OnChanges, OnDestroy
{
  @Input() accountId: number | null = null;
  @Input() initialRule: OfferRuleModel | null = null;
  @Input() initialRuleType = OfferRuleTypeEnumerator.OfferSetRule;
  @Input() initialDiscoveryFilters: QualifyingRuleSourceFilters | null = null;
  @Input() initialSelectedSource: QualifyingRuleSourceRow | null = null;
  @Input() isEditMode = false;
  @Input() preserveDependentFilters = false;
  @Input() preserveSelectedSource = false;

  @Output() ruleDetailsChange = new EventEmitter<IpopRuleWorkflowRuleDetailsValue>();
  @Output() discoveryFiltersChange = new EventEmitter<QualifyingRuleSourceFilters | null>();
  @Output() selectedSourceChange = new EventEmitter<QualifyingRuleSourceRow | null>();
  @Output() readinessChange = new EventEmitter<boolean>();
  @Output() qualifyingVehicleCountChange = new EventEmitter<number>();
  @Output() trimOptionsChange = new EventEmitter<readonly string[]>();
  @Output() sourceInvalidated = new EventEmitter<null>();

  readonly pageSize = discoveryPageSize;
  readonly anyTrimValue = anyTrimValue;
  readonly ruleTypeOptions: readonly SelectOption<OfferRuleTypeEnumerator>[] = [
    {label: 'Offer Set Rule', value: OfferRuleTypeEnumerator.OfferSetRule},
    {label: 'Dollar Discount Rule', value: OfferRuleTypeEnumerator.DollarDiscountRule}
  ];
  readonly privacyOptions: readonly SelectOption<RulePrivacyEnumerator>[] = [
    {label: 'Private', value: RulePrivacyEnumerator.Private},
    {label: 'Public', value: RulePrivacyEnumerator.Public}
  ];
  readonly vehicleTypeOptions: readonly SelectOption<VehicleTypeEnumerator>[] = [
    {label: 'Any', value: VehicleTypeEnumerator.Any},
    {label: 'New', value: VehicleTypeEnumerator.New},
    {label: 'Used', value: VehicleTypeEnumerator.Used},
    {label: 'CPO', value: VehicleTypeEnumerator.Cpo}
  ];
  readonly ruleDetailsForm = new FormGroup<RuleDetailsFormControls>({
    ruleType: new FormControl(OfferRuleTypeEnumerator.OfferSetRule, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    privacy: new FormControl(RulePrivacyEnumerator.Private, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    status: new FormControl(true, {nonNullable: true}),
    vehicleType: new FormControl(VehicleTypeEnumerator.Any, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    year: new FormControl<number | null>(null, [positiveSafeIntegerValidator]),
    make: new FormControl<string | null>(null),
    model: new FormControl<string | null>(null),
    trim: new FormControl<string | null>(anyTrimValue),
    maximumPrice: new FormControl<number | null>(null, [finiteNonNegativeValidator]),
    vehicleLimit: new FormControl<number | null>(null, [nonNegativeSafeIntegerValidator]),
    dollarDiscount: new FormControl<number | null>(null)
  });

  discoveryState: QualifyingRuleSourceDiscoveryState | null = null;
  facets: QualifyingRuleSourceFacets = this.buildEmptyFacets();
  selectedSource: QualifyingRuleSourceRow | null = null;
  currentPage = defaultPage;
  maximumPriceSortDirection: QualifyingRuleSourceMaximumPriceSortDirection = ascendingSortDirection;

  private readonly destroySubject = new Subject<void>();
  private readonly discoveryRequests = new Subject<QualifyingRuleSourceDiscoveryRequest>();
  private discoverySubscription: Subscription | null = null;
  private lastDiscoveryRequest: QualifyingRuleSourceDiscoveryRequest | null = null;
  private lastReadiness: boolean | null = null;
  private preserveDependentFiltersForNextPage = false;
  private initialized = false;

  constructor(private readonly qualifyingRuleSourceService: QualifyingRuleSourceService)
  {
  }

  /** Initializes copied input state and reactive discovery subscriptions. */
  ngOnInit(): void
  {
    this.applyInitialState();
    this.configureRuleType(false);
    this.configureEditModeControls();
    this.subscribeToEligibilityControls();
    this.subscribeToRuleDetailsChanges();
    this.initialized = true;
    this.emitSelectedSource();
    this.emitCurrentState();
    this.refreshDiscovery();
  }

  /** Reinitializes copied inputs without mutating parent-owned values. */
  ngOnChanges(changes: SimpleChanges): void
  {
    let initializationApplied = false;
    if (this.initialized && this.hasInitializationChange(changes)) {
      this.applyInitialState();
      this.preserveDependentFiltersForNextPage = true;
      this.configureRuleType(false);
      this.configureEditModeControls();
      initializationApplied = true;
    }
    const accountChanged = this.initialized && changes['accountId'] !== undefined;
    const selectedSourceChanged = this.initialized && changes['initialSelectedSource'] !== undefined;
    if (accountChanged) {
      this.handleAccountChange();
    }
    if (selectedSourceChanged && !initializationApplied && !accountChanged) {
      this.selectedSource = this.resolveInitialSelectedSource();
      this.emitReadiness();
    }
    if (initializationApplied && !accountChanged) {
      this.emitSelectedSource();
      this.emitCurrentState();
      this.refreshDiscovery();
    }
  }

  /** Releases component and in-flight discovery subscriptions. */
  ngOnDestroy(): void
  {
    this.stopDiscovery();
    this.destroySubject.next();
    this.destroySubject.complete();
    this.discoveryRequests.complete();
  }

  /** Selects one current, server-returned, selectable source. */
  onSourceSelected(source: QualifyingRuleSourceRow): void
  {
    const currentSource = this.findCurrentSource(source.offerSetId);
    if (this.isOfferSetRule() && !this.isEditMode && currentSource !== null && currentSource.selectable) {
      this.selectedSource = this.copySource(currentSource);
      this.selectedSourceChange.emit(this.copySource(currentSource));
      this.emitReadiness();
    }
  }

  /** Changes only the server page while preserving rule criteria and selection. */
  onPageChanged(page: number): void
  {
    if (Number.isSafeInteger(page) && page > 0 && page !== this.currentPage) {
      this.currentPage = page;
      this.refreshDiscovery();
    }
  }

  /** Applies Maximum Price sorting from the first server page. */
  onMaximumPriceSortDirectionChanged(direction: QualifyingRuleSourceMaximumPriceSortDirection): void
  {
    if (this.isMaximumPriceSortDirection(direction) && direction !== this.maximumPriceSortDirection) {
      this.maximumPriceSortDirection = direction;
      this.currentPage = defaultPage;
      this.refreshDiscovery();
    }
  }

  /** Reuses the current validated request through the IT-3 discovery stream. */
  onRetryRequested(): void
  {
    const request = this.lastDiscoveryRequest;
    if (request !== null && this.buildDiscoveryRequest() !== null) {
      this.stopDiscovery();
      this.startDiscovery();
      this.submitDiscoveryRequest(this.copyRequest(request));
    }
  }

  /** Indicates whether qualifying discovery can run for current inputs. */
  canDiscover(): boolean
  {
    const canDiscover = this.buildDiscoveryRequest() !== null;

    return canDiscover;
  }

  /** Indicates whether the current rule type owns qualifying discovery. */
  isOfferSetRule(): boolean
  {
    const isOfferSetRule = this.ruleDetailsForm.controls.ruleType.value
      === OfferRuleTypeEnumerator.OfferSetRule;

    return isOfferSetRule;
  }

  private subscribeToEligibilityControls(): void
  {
    const controls = this.ruleDetailsForm.controls;
    controls.ruleType.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(() => this.handleRuleTypeChange());
    controls.privacy.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(() => this.handleCoarseChange());
    controls.vehicleType.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(() => this.handleCoarseChange());
    controls.year.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(() => this.handlePreservedFilterChange());
    controls.maximumPrice.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(() => this.handlePreservedFilterChange());
    controls.make.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(() => this.handleMakeChange());
    controls.model.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(() => this.handleModelChange());
    controls.trim.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(() => this.handleEligibilityChange());
  }

  private subscribeToRuleDetailsChanges(): void
  {
    this.ruleDetailsForm.valueChanges
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        this.emitRuleDetails();
        this.emitReadiness();
      });
  }

  private handleRuleTypeChange(): void
  {
    this.configureRuleType(true);
    this.clearAllDependentControls();
    this.facets = this.buildEmptyFacets();
    this.currentPage = defaultPage;
    this.invalidateSelection();
    this.refreshDiscovery();
    this.emitCurrentState();
  }

  private handleCoarseChange(): void
  {
    this.preserveDependentFiltersForNextPage = true;
    this.handleEligibilityChange();
  }

  private handlePreservedFilterChange(): void
  {
    this.preserveDependentFiltersForNextPage = true;
    this.handleEligibilityChange();
  }

  private handleMakeChange(): void
  {
    this.preserveDependentFiltersForNextPage = false;
    this.ruleDetailsForm.controls.model.setValue(null, {emitEvent: false});
    this.ruleDetailsForm.controls.trim.setValue(anyTrimValue, {emitEvent: false});
    this.facets = {...this.facets, models: [], trims: []};
    this.handleEligibilityChange();
  }

  private handleModelChange(): void
  {
    this.preserveDependentFiltersForNextPage = false;
    this.ruleDetailsForm.controls.trim.setValue(anyTrimValue, {emitEvent: false});
    this.facets = {...this.facets, trims: []};
    this.handleEligibilityChange();
  }

  private handleEligibilityChange(): void
  {
    this.currentPage = defaultPage;
    if (this.isOfferSetRule() && !this.isEditMode && !this.preserveSelectedSource) {
      this.invalidateSelection();
    }
    this.emitCurrentState();
    this.refreshDiscovery();
  }

  private handleAccountChange(): void
  {
    this.clearAllDependentControls();
    this.facets = this.buildEmptyFacets();
    this.currentPage = defaultPage;
    this.invalidateSelection();
    this.emitCurrentState();
    this.refreshDiscovery();
  }

  private configureRuleType(clearTransitionValues: boolean): void
  {
    const controls = this.ruleDetailsForm.controls;
    this.configureCriteriaValidators(this.isOfferSetRule());
    if (this.isOfferSetRule()) {
      controls.privacy.enable({emitEvent: false});
      controls.dollarDiscount.clearValidators();
      if (clearTransitionValues) {
        controls.dollarDiscount.setValue(null, {emitEvent: false});
      }
    }
    if (!this.isOfferSetRule()) {
      controls.privacy.setValue(RulePrivacyEnumerator.Private, {emitEvent: false});
      controls.privacy.disable({emitEvent: false});
      controls.dollarDiscount.setValidators([Validators.required, finitePositiveValidator]);
    }
    controls.dollarDiscount.updateValueAndValidity({emitEvent: false});
  }

  private configureEditModeControls(): void
  {
    const controls = this.ruleDetailsForm.controls;
    if (this.isEditMode) {
      controls.ruleType.disable({emitEvent: false});
      controls.privacy.disable({emitEvent: false});
      controls.vehicleType.disable({emitEvent: false});
      controls.year.disable({emitEvent: false});
      controls.make.disable({emitEvent: false});
      controls.model.disable({emitEvent: false});
    }
    if (!this.isEditMode) {
      controls.ruleType.enable({emitEvent: false});
      controls.vehicleType.enable({emitEvent: false});
      controls.year.enable({emitEvent: false});
      controls.make.enable({emitEvent: false});
      controls.model.enable({emitEvent: false});
    }
  }

  private configureCriteriaValidators(required: boolean): void
  {
    const controls = this.ruleDetailsForm.controls;
    const yearValidators: ValidatorFn[] = [positiveSafeIntegerValidator];
    const maximumPriceValidators: ValidatorFn[] = [Validators.required, finiteNonNegativeValidator];
    const textValidators: ValidatorFn[] = [];
    if (required) {
      yearValidators.push(Validators.required);
      textValidators.push(Validators.required);
    }
    controls.year.setValidators(yearValidators);
    controls.make.setValidators(textValidators);
    controls.model.setValidators(textValidators);
    controls.trim.setValidators(textValidators);
    controls.maximumPrice.setValidators(maximumPriceValidators);
    controls.year.updateValueAndValidity({emitEvent: false});
    controls.make.updateValueAndValidity({emitEvent: false});
    controls.model.updateValueAndValidity({emitEvent: false});
    controls.trim.updateValueAndValidity({emitEvent: false});
    controls.maximumPrice.updateValueAndValidity({emitEvent: false});
  }

  private refreshDiscovery(): void
  {
    const request = this.buildDiscoveryRequest();
    if (request !== null) {
      this.startDiscovery();
      this.submitDiscoveryRequest(request);
    }
    if (request === null) {
      this.stopDiscovery();
      this.clearDiscoveryState();
      this.lastDiscoveryRequest = null;
      this.discoveryFiltersChange.emit(null);
      this.emitReadiness();
    }
  }

  private submitDiscoveryRequest(request: QualifyingRuleSourceDiscoveryRequest): void
  {
    const requestCopy = this.copyRequest(request);
    this.discoveryState = this.buildPendingState();
    this.qualifyingVehicleCountChange.emit(0);
    this.lastDiscoveryRequest = requestCopy;
    this.discoveryFiltersChange.emit({...requestCopy.filters});
    this.discoveryRequests.next(this.copyRequest(requestCopy));
    this.emitReadiness();
  }

  private startDiscovery(): void
  {
    if (this.discoverySubscription === null) {
      this.discoverySubscription = this.qualifyingRuleSourceService
        .discover(this.discoveryRequests)
        .subscribe((state) => this.applyDiscoveryState(state));
    }
  }

  private stopDiscovery(): void
  {
    if (this.discoverySubscription !== null) {
      this.discoverySubscription.unsubscribe();
      this.discoverySubscription = null;
    }
  }

  private applyDiscoveryState(state: QualifyingRuleSourceDiscoveryState): void
  {
    if (this.discoverySubscription !== null) {
      this.discoveryState = state;
      if (state.page !== null) {
        this.applyDiscoveryPage(state.page);
      }
      this.emitReadiness();
    }
  }

  private applyDiscoveryPage(page: QualifyingRuleSourcePage): void
  {
    this.facets = this.copyApplicableFacets(page.facets);
    this.currentPage = page.pagination.page;
    this.qualifyingVehicleCountChange.emit(page.pagination.totalItems);
    this.trimOptionsChange.emit([...this.facets.trims]);
    let reconciled = false;
    if (!this.isEditMode && !this.preserveDependentFilters && !this.preserveDependentFiltersForNextPage) {
      reconciled = this.reconcileDependentControls();
    }
    this.preserveDependentFiltersForNextPage = false;
    if (reconciled) {
      this.handleEligibilityChange();
    }
    if (!reconciled && !this.isEditMode && !this.preserveSelectedSource) {
      this.reconcileSelectedSource(page);
    }
  }

  private reconcileDependentControls(): boolean
  {
    const controls = this.ruleDetailsForm.controls;
    let reconciled = false;
    if (controls.make.value !== null && !this.facets.makes.includes(controls.make.value)) {
      this.clearAllDependentControls();
      reconciled = true;
    }
    if (!reconciled && controls.model.value !== null && !this.facets.models.includes(controls.model.value)) {
      controls.model.setValue(null, {emitEvent: false});
      controls.trim.setValue(anyTrimValue, {emitEvent: false});
      reconciled = true;
    }
    const trimFilter = this.normalizeTrimFilter(controls.trim.value);
    if (!reconciled && trimFilter !== null && !this.facets.trims.includes(trimFilter)) {
      controls.trim.setValue(anyTrimValue, {emitEvent: false});
      reconciled = true;
    }

    return reconciled;
  }

  private reconcileSelectedSource(page: QualifyingRuleSourcePage): void
  {
    if (this.selectedSource !== null) {
      const selectedOfferSetId = this.selectedSource.offerSetId;
      const matchingSource = page.items.find((source) => source.offerSetId === selectedOfferSetId);
      if (matchingSource !== undefined && !matchingSource.selectable) {
        this.invalidateSelection();
      }
      if (matchingSource !== undefined && matchingSource.selectable) {
        this.selectedSource = this.copySource(matchingSource);
        this.selectedSourceChange.emit(this.copySource(matchingSource));
      }
    }
  }

  private buildDiscoveryRequest(): QualifyingRuleSourceDiscoveryRequest | null
  {
    let request: QualifyingRuleSourceDiscoveryRequest | null = null;
    const filters = this.buildDiscoveryFilters();
    if (this.isValidAccountId(this.accountId) && filters !== null) {
      request = {
        accountId: this.accountId,
        filters
      };
    }

    return request;
  }

  private buildDiscoveryFilters(): QualifyingRuleSourceFilters | null
  {
    let filters: QualifyingRuleSourceFilters | null = null;
    const controls = this.ruleDetailsForm.controls;
    if (this.hasValidDiscoveryCriteria() && this.hasValidDependencies()) {
      filters = {
        year: controls.year.value,
        make: this.normalizeText(controls.make.value),
        model: this.normalizeText(controls.model.value),
        trim: this.normalizeTrimFilter(controls.trim.value),
        maximumPrice: controls.maximumPrice.value,
        vehicleType: controls.vehicleType.value,
        privacy: controls.privacy.getRawValue(),
        maximumPriceSortDirection: this.maximumPriceSortDirection,
        page: this.currentPage,
        pageSize: discoveryPageSize
      };
    }

    return filters;
  }

  private hasValidDiscoveryCriteria(): boolean
  {
    const controls = this.ruleDetailsForm.controls;
    const valid = !controls.year.hasError('positiveSafeInteger')
      && !controls.maximumPrice.hasError('finiteNonNegative');

    return valid;
  }

  private hasValidDependencies(): boolean
  {
    const controls = this.ruleDetailsForm.controls;
    let valid = true;
    if (this.normalizeText(controls.model.value) !== null && this.normalizeText(controls.make.value) === null) {
      valid = false;
    }
    if (this.normalizeTrimFilter(controls.trim.value) !== null
      && (this.normalizeText(controls.make.value) === null || this.normalizeText(controls.model.value) === null)) {
      valid = false;
    }

    return valid;
  }

  private emitCurrentState(): void
  {
    this.emitRuleDetails();
    this.emitReadiness();
  }

  private emitSelectedSource(): void
  {
    if (this.selectedSource === null) {
      this.selectedSourceChange.emit(null);
    }
    if (this.selectedSource !== null) {
      this.selectedSourceChange.emit(this.copySource(this.selectedSource));
    }
  }

  private emitRuleDetails(): void
  {
    const controls = this.ruleDetailsForm.controls;
    let status: IpopRuleWorkflowRuleStatus = inactiveStatus;
    if (controls.status.value) {
      status = activeStatus;
    }
    const details: IpopRuleWorkflowRuleDetailsValue = {
      ruleType: controls.ruleType.value,
      privacy: controls.privacy.getRawValue(),
      status,
      vehicleType: controls.vehicleType.value,
      year: controls.year.value,
      make: this.normalizeText(controls.make.value),
      model: this.normalizeText(controls.model.value),
      trim: this.normalizeText(controls.trim.value),
      maximumPrice: controls.maximumPrice.value,
      vehicleLimit: controls.vehicleLimit.value,
      dollarDiscount: controls.dollarDiscount.value
    };
    this.ruleDetailsChange.emit({...details});
  }

  private emitReadiness(): void
  {
    const ready = this.calculateReadiness();
    if (ready !== this.lastReadiness) {
      this.lastReadiness = ready;
      this.readinessChange.emit(ready);
    }
  }

  private calculateReadiness(): boolean
  {
    let ready = this.ruleDetailsForm.valid;
    const state = this.discoveryState;
    if (this.isOfferSetRule() && !this.isEditMode) {
      if (state === null
        || state.status === QualifyingRuleSourceDiscoveryStatus.Loading
        || state.status === QualifyingRuleSourceDiscoveryStatus.Error) {
        ready = false;
      }
      if (this.selectedSource === null || !this.selectedSource.selectable) {
        ready = false;
      }
    }

    return ready;
  }

  private invalidateSelection(): void
  {
    this.selectedSource = null;
    this.selectedSourceChange.emit(null);
    this.sourceInvalidated.emit(null);
    this.emitReadiness();
  }

  private findCurrentSource(offerSetId: number): QualifyingRuleSourceRow | null
  {
    let source: QualifyingRuleSourceRow | null = null;
    const page = this.discoveryState?.page;
    if (page !== null && page !== undefined) {
      const matchingSource = page.items.find((item) => item.offerSetId === offerSetId);
      if (matchingSource !== undefined) {
        source = matchingSource;
      }
    }

    return source;
  }

  private applyInitialState(): void
  {
    const initialValues = this.buildInitialValues();
    this.ruleDetailsForm.setValue(initialValues, {emitEvent: false});
    this.currentPage = this.resolveInitialPage();
    this.maximumPriceSortDirection = this.resolveInitialMaximumPriceSortDirection();
    this.normalizeDependentControls();
    this.facets = this.buildEmptyFacets();
    this.selectedSource = this.resolveInitialSelectedSource();
  }

  private buildInitialValues(): ReturnType<IpopRuleWorkflowRuleDetailsComponent['ruleDetailsForm']['getRawValue']>
  {
    const values = this.buildDefaultValues();
    this.applyInitialRule(values);
    this.applyInitialFilters(values);
    values.ruleType = this.resolveRuleType(this.initialRuleType);

    return values;
  }

  private buildDefaultValues(): ReturnType<IpopRuleWorkflowRuleDetailsComponent['ruleDetailsForm']['getRawValue']>
  {
    const values = {
      ruleType: OfferRuleTypeEnumerator.OfferSetRule,
      privacy: RulePrivacyEnumerator.Private,
      status: true,
      vehicleType: VehicleTypeEnumerator.Any,
      year: null,
      make: null,
      model: null,
      trim: anyTrimValue,
      maximumPrice: null,
      vehicleLimit: null,
      dollarDiscount: null
    };

    return values;
  }

  private applyInitialRule(
    values: ReturnType<IpopRuleWorkflowRuleDetailsComponent['ruleDetailsForm']['getRawValue']>
  ): void
  {
    if (this.initialRule !== null) {
      const rule = {...this.initialRule};
      values.privacy = this.resolvePrivacy(rule.isPrivate);
      values.status = rule.status !== inactiveStatus;
      values.vehicleType = this.resolveVehicleType(rule.vehicleCondition);
      values.year = rule.year ?? null;
      values.make = this.normalizeText(rule.make ?? null);
      values.model = this.normalizeText(rule.model ?? null);
      values.trim = this.resolveTrimControlValue(rule.trim ?? null);
      values.maximumPrice = rule.maxSalePrice ?? null;
      values.vehicleLimit = rule.vehicleLimit ?? null;
      values.dollarDiscount = rule.dollarAmountDiscount ?? null;
    }
  }

  private applyInitialFilters(
    values: ReturnType<IpopRuleWorkflowRuleDetailsComponent['ruleDetailsForm']['getRawValue']>
  ): void
  {
    if (this.initialDiscoveryFilters !== null) {
      const filters = {...this.initialDiscoveryFilters};
      values.privacy = filters.privacy;
      values.vehicleType = filters.vehicleType;
      values.year = filters.year;
      values.make = this.normalizeText(filters.make);
      values.model = this.normalizeText(filters.model);
      values.trim = this.resolveTrimControlValue(filters.trim);
      values.maximumPrice = filters.maximumPrice;
    }
  }

  private resolveInitialPage(): number
  {
    let page = defaultPage;
    const initialPage = this.initialDiscoveryFilters?.page;
    if (initialPage !== undefined && Number.isSafeInteger(initialPage) && initialPage > 0) {
      page = initialPage;
    }

    return page;
  }

  private resolveInitialMaximumPriceSortDirection(): QualifyingRuleSourceMaximumPriceSortDirection
  {
    let direction = ascendingSortDirection;
    const initialDirection = this.initialDiscoveryFilters?.maximumPriceSortDirection;
    if (initialDirection !== undefined && this.isMaximumPriceSortDirection(initialDirection)) {
      direction = initialDirection;
    }

    return direction;
  }

  private resolveInitialSelectedSource(): QualifyingRuleSourceRow | null
  {
    let source: QualifyingRuleSourceRow | null = null;
    const initialSource = this.initialSelectedSource;
    if (this.resolveRuleType(this.initialRuleType) === OfferRuleTypeEnumerator.OfferSetRule
      && initialSource !== null
      && initialSource.selectable
      && Number.isSafeInteger(initialSource.offerSetId)
      && initialSource.offerSetId > 0) {
      source = this.copySource(initialSource);
    }

    return source;
  }

  private normalizeDependentControls(): void
  {
    const controls = this.ruleDetailsForm.controls;
    if (this.normalizeText(controls.make.value) === null) {
      controls.make.setValue(null, {emitEvent: false});
      controls.model.setValue(null, {emitEvent: false});
      controls.trim.setValue(anyTrimValue, {emitEvent: false});
    }
    if (this.normalizeText(controls.model.value) === null) {
      controls.model.setValue(null, {emitEvent: false});
      controls.trim.setValue(anyTrimValue, {emitEvent: false});
    }
    if (this.normalizeText(controls.trim.value) === null) {
      controls.trim.setValue(anyTrimValue, {emitEvent: false});
    }
  }

  private clearAllDependentControls(): void
  {
    const controls = this.ruleDetailsForm.controls;
    controls.make.setValue(null, {emitEvent: false});
    controls.model.setValue(null, {emitEvent: false});
    controls.trim.setValue(anyTrimValue, {emitEvent: false});
  }

  private clearDiscoveryState(): void
  {
    this.discoveryState = null;
    this.lastDiscoveryRequest = null;
    this.qualifyingVehicleCountChange.emit(0);
  }

  private buildPendingState(): QualifyingRuleSourceDiscoveryState
  {
    const state: QualifyingRuleSourceDiscoveryState = {
      status: QualifyingRuleSourceDiscoveryStatus.Loading,
      page: null,
      error: null,
      requestId: pendingRequestId
    };

    return state;
  }

  private buildEmptyFacets(): QualifyingRuleSourceFacets
  {
    const facets: QualifyingRuleSourceFacets = {
      years: [],
      makes: [],
      models: [],
      trims: []
    };

    return facets;
  }

  private copyApplicableFacets(facets: QualifyingRuleSourceFacets): QualifyingRuleSourceFacets
  {
    const controls = this.ruleDetailsForm.controls;
    let models: readonly string[] = [];
    let trims: readonly string[] = [];
    if (this.normalizeText(controls.make.value) !== null) {
      models = [...facets.models];
    }
    if (this.normalizeText(controls.make.value) !== null && this.normalizeText(controls.model.value) !== null) {
      trims = [...facets.trims];
    }
    const facetsCopy: QualifyingRuleSourceFacets = {
      years: [...facets.years],
      makes: [...facets.makes],
      models,
      trims
    };

    return facetsCopy;
  }

  private copyRequest(request: QualifyingRuleSourceDiscoveryRequest): QualifyingRuleSourceDiscoveryRequest
  {
    const requestCopy: QualifyingRuleSourceDiscoveryRequest = {
      accountId: request.accountId,
      filters: {...request.filters}
    };

    return requestCopy;
  }

  private copySource(source: QualifyingRuleSourceRow): QualifyingRuleSourceRow
  {
    const sourceCopy: QualifyingRuleSourceRow = {...source};

    return sourceCopy;
  }

  private normalizeText(value: string | null): string | null
  {
    let normalizedValue: string | null = null;
    if (value !== null && value.trim() !== '') {
      normalizedValue = value.trim();
    }

    return normalizedValue;
  }

  private normalizeTrimFilter(value: string | null): string | null
  {
    let normalizedValue = this.normalizeText(value);
    if (normalizedValue === anyTrimValue) {
      normalizedValue = null;
    }

    return normalizedValue;
  }

  private resolveTrimControlValue(value: string | null): string
  {
    let resolvedValue = anyTrimValue;
    const normalizedValue = this.normalizeText(value);
    if (normalizedValue !== null) {
      resolvedValue = normalizedValue;
    }

    return resolvedValue;
  }

  private isValidAccountId(accountId: number | null): accountId is number
  {
    const valid = accountId !== null && Number.isSafeInteger(accountId) && accountId > 0;

    return valid;
  }

  private isMaximumPriceSortDirection(value: string): value is QualifyingRuleSourceMaximumPriceSortDirection
  {
    const valid = value === ascendingSortDirection || value === descendingSortDirection;

    return valid;
  }

  private resolvePrivacy(isPrivate: boolean | undefined): RulePrivacyEnumerator
  {
    let privacy = RulePrivacyEnumerator.Private;
    if (isPrivate === false) {
      privacy = RulePrivacyEnumerator.Public;
    }

    return privacy;
  }

  private resolveVehicleType(vehicleCondition: string | undefined): VehicleTypeEnumerator
  {
    let vehicleType = VehicleTypeEnumerator.Any;
    if (vehicleCondition === 'New') {
      vehicleType = VehicleTypeEnumerator.New;
    }
    if (vehicleCondition === 'Used') {
      vehicleType = VehicleTypeEnumerator.Used;
    }
    if (vehicleCondition === 'CPO') {
      vehicleType = VehicleTypeEnumerator.Cpo;
    }

    return vehicleType;
  }

  private resolveRuleType(ruleType: OfferRuleTypeEnumerator): OfferRuleTypeEnumerator
  {
    let resolvedRuleType = OfferRuleTypeEnumerator.OfferSetRule;
    if (ruleType === OfferRuleTypeEnumerator.DollarDiscountRule) {
      resolvedRuleType = OfferRuleTypeEnumerator.DollarDiscountRule;
    }

    return resolvedRuleType;
  }

  private hasInitializationChange(changes: SimpleChanges): boolean
  {
    const changed = changes['initialRule'] !== undefined
      || changes['initialRuleType'] !== undefined
      || changes['initialDiscoveryFilters'] !== undefined
      || changes['isEditMode'] !== undefined;

    return changed;
  }
}
